import React from "react";
import RaciTaskRow from "./RaciTaskRow";
import {
  RACI_ROLES,
  ROLE_LABELS,
  PARTICIPANT_CURRENCY,
} from "../../utils/whatif/whatIfNormalize";
import {
  formatAmount,
  formatCount,
  formatDurationShort,
  formatPercent,
} from "../../utils/whatif/whatIfFormat";

const GRID_COLUMNS = `2.4fr ${RACI_ROLES.map(() => "1fr").join(" ")}`;

const Summary = ({ label, value }) => (
  <div className="wif-raci-summary-item">
    <span className="wif-raci-summary-label">{label}</span>
    <span className="wif-raci-summary-value">{value}</span>
  </div>
);

// Unlike Summary, this shows the transition itself: what the matrix would
// have cost with every row still staffed (`original`, including rows the
// backend already dropped) next to what it actually costs once those rows
// are excluded (`current`).
const CostSummary = ({ label, original, current, money }) => {
  const delta = current - original;
  const percent = original !== 0 ? (delta / original) * 100 : 0;
  const direction = delta > 0 ? "up" : delta < 0 ? "down" : "flat";

  return (
    <div className="wif-raci-summary-item">
      <span className="wif-raci-summary-label">{label}</span>
      <span className="wif-raci-summary-value">
        {money(original)}
        <span className="wif-compare-arrow" aria-hidden="true">
          {" "}
          →{" "}
        </span>
        {money(current)}
      </span>
      {delta !== 0 && (
        <span className={`wif-delta wif-delta--${direction}`}>
          {delta >= 0 ? "+" : "−"}
          {money(Math.abs(delta))}
          {original !== 0 && ` (${formatPercent(percent, 1)})`}
        </span>
      )}
    </div>
  );
};

const RaciMatrix = ({ tasks, totals, timeUnit, currency, rates }) => {
  if (!tasks.length) {
    return (
      <div className="role-card">
        <div className="section-title">RACI Assignments</div>
        <p className="muted">
          This process has no tasks with RACI staffing recorded.
        </p>
      </div>
    );
  }

  // Eliminated rows are still rendered — they are part of the as-is picture —
  // but the backend has already removed them from `total_process_cost_usd`, so
  // anything summed here has to leave them out to match the cost beside it.
  const counted = tasks.filter((task) => !task.eliminated);
  const eliminatedCount = tasks.length - counted.length;
  const totalMinutes = counted.reduce((sum, task) => sum + task.minutesTotal, 0);
  const totalPeople = counted.reduce(
    (sum, task) => sum + task.participantCount,
    0,
  );

  // Every task's own cost is reported in full regardless of elimination (see
  // `totalCostUsd` in whatIfNormalize.js), so summing across all of them —
  // eliminated rows included — recovers what the matrix would have cost before
  // any task was dropped, without needing a dedicated field from the backend.
  const grossOriginalTotalUsd = tasks.reduce(
    (sum, task) => sum + task.totalCostUsd,
    0,
  );
  const money = (amount) =>
    formatAmount(amount, PARTICIPANT_CURRENCY, currency, rates);

  return (
    <div className="role-card">
      <div className="section-title">RACI Assignments (As-Is)</div>

      <div className="roles-table wif-raci-table">
        <div
          className="roles-table-header wif-raci-header-row"
          style={{ gridTemplateColumns: GRID_COLUMNS }}
        >
          <div className="cell">Task</div>
          {RACI_ROLES.map((role) => (
            <div className={`cell wif-raci-head wif-raci-head--${role}`} key={role}>
              <span className="wif-raci-dot" aria-hidden="true" />
              {ROLE_LABELS[role]} <span className="wif-raci-head-key">({role})</span>
            </div>
          ))}
        </div>

        <div className="roles-table-body as-table">
          {tasks.map((task) => (
            <RaciTaskRow
              key={task.taskId}
              task={task}
              gridTemplateColumns={GRID_COLUMNS}
              timeUnit={timeUnit}
              currency={currency}
              rates={rates}
            />
          ))}
        </div>

        {/* Totals sit in their own strip rather than inside the grid, where they
            would have to borrow role columns that do not describe them. */}
        <div className="wif-raci-summary">
          <Summary
            label="Tasks"
            value={
              eliminatedCount > 0
                ? `${formatCount(counted.length)} (${formatCount(eliminatedCount)} NVA)`
                : formatCount(tasks.length)
            }
          />
          <Summary label="Assignments" value={formatCount(totalPeople)} />
          <Summary
            label="Attributed effort"
            value={formatDurationShort(totalMinutes, timeUnit)}
          />
          {/* As-is is the gross sum above (every row, elimination or not);
              to-be is `to_be_total_process_cost_usd` — what the process
              actually costs once the NVA tasks the rows above still show have
              been deducted. */}
          <CostSummary
            label="Staffing cost"
            original={grossOriginalTotalUsd}
            current={totals.toBeTotalUsd}
            money={money}
          />
        </div>
      </div>
    </div>
  );
};

export default RaciMatrix;
